/**
 * Others reading a PDF with you, as in a live document: pointers (office/sync/pointers.ts) and what's selected in its
 * text (textPlaces.ts, PdfSelections.tsx). Yours shared with the others in its room (its presence `state`: there's no
 * live document under a PDF), theirs drawn; a pointer out of view (a page not near the screen), an arrow at the edge
 * pointing to them.
 */

import { useEffect, useMemo, useRef } from "preact/hooks";
import type { PresencePeer } from "../../api/types.gen.ts";
import { displayName } from "../../lib/people.ts";
import { usePresence } from "../../lib/presence.ts";
import { colourOf } from "../../lib/presenceLook.ts";
import { Pointers } from "../../office/sync/PointerLayer.tsx";
import { isPointed, type Pointed, type Pointing, useMyPointer } from "../../office/sync/pointers.ts";
import { PdfSelections, type Selecting } from "./PdfSelections.tsx";
import { pdfPointerSpace } from "./pointerSpace.ts";
import { isTextSelection, selectionIn, type TextSelection } from "./textPlaces.ts";

/** What a reader shares: their pointer's latest frame, and their selection. */
interface Shared {
  pointer?: Pointed | null;
  sel?: TextSelection | null;
}

/** A number for an entry (its id's), as a live document's clients have. */
function clientOf(id: string): number {
  let h = 0;
  for (const c of id) {
    h = (Math.imul(h, 31) + (c.codePointAt(0) ?? 0)) | 0;
  }
  return h >>> 0;
}

const sharedOf = (p: PresencePeer): Shared => (p.state ?? {}) as Shared;
const colour = (p: PresencePeer) => p.color || colourOf(p.user);

/** Others in the room pointing (their state's `pointer`). */
function pointing(peers: readonly PresencePeer[]): Pointing[] {
  return peers.flatMap((p) => {
    const { pointer } = sharedOf(p);
    return isPointed(pointer)
      ? [{ client: clientOf(p.id), name: displayName(p.user, p.name), colour: colour(p), pointer }]
      : [];
  });
}

/** Others in the room, what they've selected (their state's `sel`). */
const selecting = (peers: readonly PresencePeer[]): Selecting[] =>
  peers.map((p) => {
    const { pointer, sel } = sharedOf(p);
    return { entry: p.id, colour: colour(p), sel: isTextSelection(sel) ? sel : null, pointing: isPointed(pointer) };
  });

/** Your selection in the PDF in `scroller`, `share`d as it changes (null once it's gone or elsewhere). */
function useMySelection(scroller: { current: HTMLElement | null }, share: (s: Shared) => void): void {
  useEffect(() => {
    let last = "null";
    const changed = () => {
      const root = scroller.current;
      const sel = root ? selectionIn(root) : null;
      const now = JSON.stringify(sel);
      if (now !== last) {
        last = now;
        share({ sel });
      }
    };
    document.addEventListener("selectionchange", changed);
    return () => {
      document.removeEventListener("selectionchange", changed);
      if (last !== "null") {
        share({ sel: null });
      }
    };
  }, [scroller, share]);
}

/** Others reading the PDF in `scroller` (at `zoom`), in presence room `room` as `view`. */
export function PdfPresence(props: {
  room: string;
  view: string;
  scroller: { current: HTMLElement | null };
  zoom: number;
}) {
  const { scroller } = props;
  const { peers, setState } = usePresence(props.room, props.view);
  const zoom = useRef(props.zoom);
  zoom.current = props.zoom;
  const say = useRef(setState);
  say.current = setState;
  const space = useMemo(
    () =>
      pdfPointerSpace(
        () => scroller.current,
        () => zoom.current,
      ),
    [scroller],
  );
  // What you share, said whole through the latest `setState` (a new one each render), the sender kept.
  const mine = useRef<Shared>({});
  const share = useMemo(
    () => (change: Shared) => {
      mine.current = { ...mine.current, ...change };
      say.current(mine.current);
    },
    [],
  );
  const send = useMemo(() => (pointer: Pointed | null) => share({ pointer }), [share]);
  useMyPointer(scroller, space, send);
  useMySelection(scroller, share);
  return (
    <>
      <PdfSelections scroller={scroller} people={selecting(peers)} zoom={props.zoom} />
      <Pointers cursors={pointing(peers)} space={space} />
    </>
  );
}
